import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  TextInput,
  Button,
  P,
  Divider,
  Grid,
  Box,
  H2,
  Spacer,
  Small,
  Flex,
  H3,
  Toggle,
} from "orcs-design-system";
import { isEqual, merge } from "lodash";
import ErrorNotification from "src/components/ErrorNotification";
import FancyCard, {
  FancyCardBody,
  FancyCardFooter,
} from "src/components/FancyCard/FancyCard";
import {
  useOrgBrandingForTenant,
  useOrgBrandingForWorkspace,
} from "src/hooks/orgConfig";

import { combineBranding } from "src/hooks/orgConfig/utils";
import ImageDropZone from "./sub-components/ImageDropZone";
import HeaderPreview from "./sub-components/HeaderPreview";
import TeamHeaderEditor from "./sub-components/TeamHeaderEditor";
import ImageLibrary from "./sub-components/ImageLibrary";

const Branding = ({
  title = "Branding options for TeamForm",
  loading = false,
  branding = null,
  processBrandingForPreview = (x) => x,
  updateBranding,
  showStockImagesToggle = true,
}) => {
  const [editedBranding, setEditedBranding] = useState(branding);
  const [savedBranding, setSavedBranding] = useState(branding);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading) {
      setEditedBranding(branding ?? {});
      setSavedBranding(branding ?? {});
    }
  }, [branding, loading]);

  const isChanged = useMemo(() => {
    return !isEqual(editedBranding, savedBranding);
  }, [editedBranding, savedBranding]);

  const makeChangeHandler = (getChange) => {
    return (valueOrEvent) => {
      setEditedBranding((prev) => {
        // merge with existing values
        return merge({}, prev ?? {}, getChange(valueOrEvent));
      });
    };
  };

  const handleRemoveLogo = useCallback(
    () =>
      setEditedBranding((prev) => {
        return merge({}, prev, { logoImage: { src: null } });
      }),
    [setEditedBranding]
  );

  const handleOnSave = useCallback(async () => {
    try {
      setIsSaving(true);

      const success = await updateBranding(editedBranding);
      if (!success) {
        throw new Error("Failed to update branding");
      }
    } catch (e) {
      setError(e);
    } finally {
      setIsSaving(false);
    }
  }, [editedBranding, updateBranding]);

  const revertChanges = useCallback(() => {
    setEditedBranding(savedBranding);
  }, [savedBranding]);

  const brandingForPreview = useMemo(() => {
    return processBrandingForPreview(editedBranding);
  }, [editedBranding, processBrandingForPreview]);

  return (
    <FancyCard height="100%">
      <FancyCardBody>
        <Box p="xs">
          <Box mb="r">
            <H2 mb="xs" fontSize="2rem">
              {title}
            </H2>
          </Box>
          <Divider />
          <Grid gridTemplateColumns="1fr 1fr" gridGap="l">
            <Box pt="r">
              <Spacer mb="r">
                <TextInput
                  type="text"
                  id="appName"
                  label="App name"
                  placeholder="TeamForm"
                  value={editedBranding?.appName ?? ""}
                  onChange={makeChangeHandler((e) => ({
                    appName: e.target.value,
                  }))}
                  disabled={isSaving}
                />
                <Box>
                  <P mb="s">Upload your company logo</P>
                  <Small display="block" mb="s">
                    Supports uploading a .png or .svg file. SVG is preferable
                    for clarity on high resolution screens.
                  </Small>
                  <Small display="block" mb="s">
                    Ideally the logo should be either an equal height/width
                    logomark, or a full horizontal logo.
                  </Small>
                  <Small display="block">
                    If using PNG ideal height is 100px, if using SVG ideal
                    height is 50px.
                  </Small>
                </Box>
                <ImageDropZone
                  onFileImported={makeChangeHandler((val) => ({
                    logoImage: { src: val },
                  }))}
                  btnLabel="Upload"
                  disabled={isSaving}
                  onRemove={handleRemoveLogo}
                  showRemove={!!editedBranding?.logoImage?.src}
                />
                <TextInput
                  name="appLogoAlt"
                  type="text"
                  id="logo-image-alt"
                  label="App logo image alt"
                  placeholder="Company name"
                  value={editedBranding?.logoImage?.alt ?? ""}
                  onChange={makeChangeHandler((e) => ({
                    logoImage: { alt: e.target.value },
                  }))}
                  disabled={isSaving || !editedBranding?.logoImage?.src}
                />
                <TextInput
                  name="appLogoStyles"
                  type="text"
                  id="logo-image-styles"
                  label="App logo image styles"
                  placeholder="CSS"
                  fullWidth
                  value={editedBranding?.logoImage?.styles ?? ""}
                  onChange={makeChangeHandler((e) => ({
                    logoImage: { styles: e.target.value },
                  }))}
                  disabled={isSaving || !editedBranding?.logoImage?.src}
                />
              </Spacer>
            </Box>
            <Box pt="r">
              <P mb="s">
                <strong>App header preview:</strong>
              </P>
              <HeaderPreview config={brandingForPreview} />
            </Box>
          </Grid>
          <Divider />
          <TeamHeaderEditor
            value={editedBranding?.gradients?.[0] ?? ""}
            onChange={makeChangeHandler((val) => ({ gradients: [val] }))}
            disabled={isSaving}
          />
        </Box>
        {showStockImagesToggle && (
          <Box py="xl" height="800px">
            <Divider mb="xl" />
            <Flex>
              <Box mb="xl">
                <H3>Upload a selection of brand approved imagery</H3>
                <P>
                  These will be available for users to select as images for
                  teams, people or tags.
                </P>
              </Box>
              <Box ml="auto">
                <Toggle
                  id="stock-images-disabled"
                  label="Stock images disabled"
                  checked={!!editedBranding?.stockImagesDisabled}
                  onChange={makeChangeHandler((e) => ({
                    stockImagesDisabled: e.target.checked,
                  }))}
                />
              </Box>
            </Flex>
            <ImageLibrary />
          </Box>
        )}
      </FancyCardBody>
      <FancyCardFooter>
        <Flex gap="s">
          <Button
            disabled={!isChanged}
            onClick={handleOnSave}
            isLoading={isSaving}
            variant={isSaving ? "disabled" : "success"}
          >
            Save changes
          </Button>
          <Button
            disabled={!isChanged}
            onClick={revertChanges}
            isLoading={isSaving}
            variant="ghost"
          >
            Revert changes
          </Button>
          {error && <ErrorNotification error={error} />}
        </Flex>
      </FancyCardFooter>
    </FancyCard>
  );
};

Branding.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  branding: PropTypes.object,
  processBrandingForPreview: PropTypes.func,
  updateBranding: PropTypes.func.isRequired,
  showStockImagesToggle: PropTypes.bool,
};

export const TenantBrandingEditor = () => {
  const { branding, loading, update } = useOrgBrandingForTenant();

  return (
    <Branding
      title="Branding options for TeamForm"
      loading={loading}
      branding={branding}
      updateBranding={update}
      showStockImagesToggle={true}
    />
  );
};

export const WorkspaceBrandingEditor = () => {
  const {
    branding: workspaceBranding,
    loading: loadingWorkspace,
    update,
  } = useOrgBrandingForWorkspace();

  const { branding: tenantBranding, loading: loadingTenant } =
    useOrgBrandingForTenant();

  const processBrandingForPreview = useCallback(
    (newBranding) => {
      return combineBranding({
        tenantBranding,
        workspaceBranding: newBranding,
      });
    },
    [tenantBranding]
  );

  return (
    <Branding
      title="Branding options for TeamForm (overrides tenant branding)."
      loading={loadingWorkspace || loadingTenant}
      branding={workspaceBranding}
      processBrandingForPreview={processBrandingForPreview}
      updateBranding={update}
      showStockImagesToggle={false}
    />
  );
};
